import { tryGetDirectory, readTextFiles, writeFile } from "../../storage/fsa.ts";
import { writeModuleIndex } from "../shared/module-index.ts";
import { renderCalendarIcs } from "./ics.ts";
import { calendarRawDir, calendarIcsPath } from "./paths.ts";
import type { CalendarEvent } from "./types.ts";

/**
 * Rebuilds one calendar's `.ics` from the raw per-event JSON files already on
 * disk — the same idempotent, network-free, read-from-disk-not-memory
 * pattern as Contacts' vCard/CSV render pass and Mail's `_index.json`. Safe
 * to re-run after every resume.
 *
 * Returns the same events so the caller (exportCalendar) can fold them into
 * the whole-mailbox calendar/index.html without a second disk read.
 */
export async function renderCalendarFile(
    root: FileSystemDirectoryHandle,
    key: string,
    calendarName: string
): Promise<CalendarEvent[]> {
    const dir = await tryGetDirectory(root, calendarRawDir(key).split("/"));
    if (!dir) {
        return [];
    }

    const events: CalendarEvent[] = [];
    for await (const { name, text } of readTextFiles(dir)) {
        if (!name.endsWith(".json")) {
            continue;
        }
        try {
            events.push(JSON.parse(text) as CalendarEvent);
        } catch {
            // A corrupt raw file is skipped rather than failing the whole
            // render — it stays on disk, visible via export.log or manual
            // review, just absent from this pass's .ics.
        }
    }

    events.sort((a, b) => (a.start?.dateTime ?? "").localeCompare(b.start?.dateTime ?? ""));
    await writeFile(root, calendarIcsPath(key), renderCalendarIcs(calendarName, events));
    return events;
}

export interface CalendarEventGroup {
    key: string;
    calendarName: string;
    events: readonly CalendarEvent[];
}

function formatEventTime(value: string | undefined): string {
    if (!value) {
        return "";
    }
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? value : new Date(ms).toLocaleString();
}

/**
 * Rebuilds `calendar/index.html` — one row per event across every calendar —
 * from the event lists `renderCalendarFile` already produced this run. Each
 * row links to its whole calendar's .ics (there is no separate per-event
 * file — Calendar's only real output is one .ics per calendar).
 */
export async function renderCalendarIndex(
    root: FileSystemDirectoryHandle,
    groups: readonly CalendarEventGroup[]
): Promise<void> {
    const rows = groups.flatMap(({ key, calendarName, events }) =>
        events
            .filter((e) => !e.isCancelled)
            .map((e) => ({
                cells: {
                    subject: e.subject || "(no subject)",
                    start: formatEventTime(e.start?.dateTime),
                    end: formatEventTime(e.end?.dateTime),
                    calendar: calendarName,
                    location: e.location?.displayName ?? ""
                },
                sortValues: { start: Date.parse(e.start?.dateTime ?? "") || 0 },
                href: `${key}.ics`
            }))
    );

    await writeModuleIndex(root, {
        moduleId: "calendar",
        itemKind: "data",
        title: "Calendar",
        subtitle: `${rows.length} event${rows.length === 1 ? "" : "s"} across ${groups.length} calendar${
            groups.length === 1 ? "" : "s"
        }`,
        columns: [
            { key: "subject", label: "Subject" },
            { key: "start", label: "Start" },
            { key: "end", label: "End" },
            { key: "calendar", label: "Calendar" },
            { key: "location", label: "Location" }
        ],
        rows,
        emptyMessage: "No events exported yet.",
        searchPlaceholder: "Filter events…"
    });
}
